import React, { useState, useEffect } from 'react';
import { Table, Text, Container, Spacer, Loading } from "@nextui-org/react";
import { useNavigate } from 'react-router-dom';
import { getWorkflows } from '../../config/workflowsApi';

interface Workflow {
  id: string;
  title?: string;
  name?: string; // Sometimes they use name instead of title
  description: string;
}

const WorkflowList = () => {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchWorkflows = async () => {
      setIsLoading(true);
      try {
        const data = await getWorkflows();
        setWorkflows(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Error fetching workflows:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchWorkflows();
  }, []);

  const columns = [
    { name: "NAME", uid: "name" },
    { name: "DESCRIPTION", uid: "description" },
  ];

  const renderCell = (item: Workflow, columnKey: React.Key) => {
    switch (columnKey) {
      case "name":
        return (
          <Text b size={14}>
            {item.title || item.name || 'Untitled Workflow'}
          </Text>
        );
      case "description":
        return (
          <Text size={14}>
            {item.description || ''}
          </Text>
        );
      default:
        return <Text>{String(item[columnKey as keyof Workflow])}</Text>;
    }
  };

  return (
    <Container md>
      <Spacer y={1} />
      <Text h2>Workflows</Text>
      <Spacer y={1} />

      {isLoading ? (
        <Loading type="points" />
      ) : (
        <Table
          aria-label="Workflows table"
          css={{
            height: "auto",
            minWidth: "100%",
          }}
          selectionMode="single"
          onSelectionChange={(selection) => {
            if (selection !== "all") {
              const selectedId = Array.from(selection)[0];
              if (selectedId) {
                navigate(`/workflows/${selectedId}`);
              }
            }
          }}
        >
          <Table.Header columns={columns}>
            {(column) => (
              <Table.Column
                key={column.uid}
                align="start"
              >
                {column.name}
              </Table.Column>
            )}
          </Table.Header>
          <Table.Body items={workflows}>
            {(item: Workflow) => (
              <Table.Row key={item.id}>
                {(columnKey: React.Key) => <Table.Cell>{renderCell(item, columnKey)}</Table.Cell>}
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      )}
    </Container>
  );
};

export default WorkflowList;
